import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import AvailabilityPanel from "../../components/director/AvailabilityPanel";
import CustomerUnitTimeline from "../../components/director/CustomerUnitTimeline";
import QualityBanner from "../../components/director/QualityBanner";
import { useDirectorActions, useDirectorContext } from "@contexts/director";
import { compactCents, formatBasis } from "../../utils/directorFormat";

const CUSTOMER_REF = /^[a-f0-9]{64}$/;
const UNLINKED = "__unlinked__";

const fixedBps = (value) => (
  Number.isSafeInteger(value)
    ? `${new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value / 100)}%`
    : "Indisponível"
);

const safeQuery = (values) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== null && value !== undefined && value !== "") query.set(key, String(value));
  }
  return query;
};

const CustomerReceiptDetail = () => {
  const { customer_ref: customerRef } = useParams();
  const navigate = useNavigate();
  const state = useDirectorContext();
  const actions = useDirectorActions();
  const {
    selectedCompanyId,
    accessByCompany,
    metadata,
    period,
    filters,
    receivableFilters,
    customerUnits,
    customerUnitsError,
    loading,
  } = state;
  const [selectedUnitKey, setSelectedUnitKey] = useState(null);
  const validRef = CUSTOMER_REF.test(customerRef || "");
  const basis = customerUnits?.basis || receivableFilters?.basis || "nominal";

  useEffect(() => {
    if (!validRef || !selectedCompanyId) return;
    actions?.loadCustomerUnits(customerRef, { unitRef: null, page: 1, pageSize: 100 });
  }, [
    actions,
    validRef,
    selectedCompanyId,
    customerRef,
    period.from,
    period.to,
    filters.legalEntityId,
    filters.projectKey,
    receivableFilters.basis,
  ]);

  const items = customerUnits?.items || [];
  const itemSignature = items.map((item) => item.unitRef || UNLINKED).join("|");
  useEffect(() => {
    if (!items.length) {
      setSelectedUnitKey(null);
      return;
    }
    setSelectedUnitKey((current) => (
      current && items.some((item) => (item.unitRef || UNLINKED) === current)
        ? current
        : (items[0].unitRef || UNLINKED)
    ));
  }, [itemSignature]);

  const selectedUnit = useMemo(() => (
    items.find((item) => (item.unitRef || UNLINKED) === selectedUnitKey) || items[0] || null
  ), [items, selectedUnitKey]);
  const customer = customerUnits?.customer;
  const access = accessByCompany[selectedCompanyId] || {};
  const project = (metadata?.projects || []).find((item) => item.key === filters.projectKey);

  if (!validRef) return <main className="director-page">
    <section className="director-state director-state--error" role="alert">
      <strong>Referência de cliente inválida</strong>
      <p>Volte à lista e abra um cliente autorizado.</p>
    </section>
  </main>;

  const retry = () => actions?.loadCustomerUnits(
    customerRef,
    { unitRef: null, page: 1, pageSize: 100 },
  );
  if (loading.customerUnits && !customerUnits) return <main className="director-page">
    <section className="director-state" role="status">Carregando unidades do cliente…</section>
  </main>;
  if (customerUnitsError?.code === "customer_not_found") return <main className="director-page">
    <section className="director-state" role="status">
      <strong>Cliente não encontrado</strong>
      <p>O cliente não está disponível no portfólio autorizado.</p>
    </section>
  </main>;
  if (customerUnitsError) return <main className="director-page">
    <AvailabilityPanel
      message={customerUnitsError.message || null}
      onRetry={customerUnitsError.retryable ? retry : null}
      state="error"
    />
  </main>;
  if (!customerUnits || !selectedUnit) return <main className="director-page">
    <AvailabilityPanel state="empty" />
  </main>;

  const keyHandover = selectedUnit.keyHandover || customer.keyHandover;
  const backFilters = customerUnits.backLinkFilters || {};
  const impactQuery = safeQuery({
    company_id: selectedCompanyId,
    project_key: filters.projectKey,
    from: period.from,
    to: period.to,
    basis,
  });
  const listQuery = safeQuery({ company_id: selectedCompanyId });
  const listPath = `/dashboard/director/customer-receipts?${listQuery}`;
  const backToList = (event) => {
    event.preventDefault();
    actions?.setFilters({
      legalEntityId: backFilters.legalEntityId ?? filters.legalEntityId,
      projectKey: backFilters.projectKey ?? filters.projectKey,
      costCenterId: null,
    });
    actions?.setPeriod({
      from: backFilters.from ?? period.from,
      to: backFilters.to ?? period.to,
    });
    actions?.setReceivableFilters({
      customerRef,
      unitRef: selectedUnit.unitRef,
      basis: backFilters.basis ?? basis,
      page: 1,
    });
    navigate(listPath);
  };

  return <main className="director-page director-receivables-page">
    <header className="director-page__header">
      <div>
        <span className="director-eyebrow">Diretoria · {access.companyName || "grupo econômico"}</span>
        <h1>Cliente e recebimentos da unidade</h1>
        <p>Contrato, baixas e parcelas futuras na mesma base; correção e chaves ficam separadas.</p>
      </div>
      <span className="director-receivables-basis-chip">Base {formatBasis(basis).toLowerCase()}</span>
    </header>

    <QualityBanner sources={[customerUnits, customer, selectedUnit]} />

    <section aria-label="Filtros do cliente" className="director-filters director-receivables-detail-filters">
      <div className="director-filter-field">
        <label htmlFor="detail-customer">Cliente</label>
        <input id="detail-customer" readOnly value={customer.displayName || "Cliente"} />
      </div>
      <div className="director-filter-field">
        <label htmlFor="detail-unit">Unidade</label>
        <select
          id="detail-unit"
          onChange={(event) => {
            const key = event.target.value;
            setSelectedUnitKey(key);
            actions?.setReceivableFilters({
              unitRef: key === UNLINKED ? null : key,
              page: 1,
            });
          }}
          value={selectedUnit.unitRef || UNLINKED}
        >
          {items.map((item) => <option
            key={item.unitRef || UNLINKED}
            value={item.unitRef || UNLINKED}
          >
            {item.unitRef ? item.displayName || "Unidade" : "Recebimentos sem unidade"}
          </option>)}
        </select>
      </div>
      <div className="director-filter-field">
        <label htmlFor="detail-project">Empreendimento</label>
        <input id="detail-project" readOnly value={project?.name || "Empreendimento"} />
      </div>
      <div className="director-filter-field">
        <label htmlFor="detail-basis">Base dos valores</label>
        <select
          id="detail-basis"
          onChange={(event) => actions?.setReceivableFilters({
            basis: event.target.value,
            page: 1,
          })}
          value={basis}
        >
          <option value="nominal">Nominal</option>
          <option
            disabled={customerUnits.basisOptions?.corrected?.state !== "available"}
            value="corrected"
          >
            Corrigida
          </option>
        </select>
      </div>
    </section>

    <section aria-label="Resumo da unidade" className="director-kpi-grid director-receivables-detail-kpis">
      <article className="director-kpi director-kpi--neutral">
        <span>Valor contratado</span><strong>{compactCents(selectedUnit.contractedAmountCents)}</strong>
        <small>Base {formatBasis(basis).toLowerCase()} · 1 unidade</small>
      </article>
      <article className="director-kpi director-kpi--positive">
        <span>Efetivamente recebido</span><strong>{compactCents(selectedUnit.receivedAmountCents)}</strong>
        <small>{fixedBps(selectedUnit.receivedPctBps)} do contrato {formatBasis(basis).toLowerCase()}</small>
      </article>
      <article className="director-kpi director-kpi--inflow">
        <span>Ainda previsto</span><strong>{compactCents(selectedUnit.scheduledAmountCents)}</strong>
        <small>Parcelas abertas; chaves fora</small>
      </article>
    </section>

    <div className="director-receivables-layout">
      <CustomerUnitTimeline
        basis={basis}
        keyHandover={keyHandover}
        points={selectedUnit.timeline || []}
      />
      <aside className="director-section director-receivables-interpretation">
        <span className="director-eyebrow">Como interpretar</span>
        <h2>{fixedBps(selectedUnit.receivedPctBps)} do contrato {formatBasis(basis).toLowerCase()}</h2>
        <p>{compactCents(selectedUnit.receivedAmountCents)} vieram de baixas confirmadas. O restante continua previsto na mesma base.</p>
        <AvailabilityPanel
          reasonCode={keyHandover?.reasonCode || "sales_contracts_restricted_or_rule_missing"}
          state={keyHandover?.state || "unavailable"}
        />
        <div className="director-receivables-actions">
          <a
            className="director-link-button"
            href={`/dashboard/director/cashflow?${impactQuery}`}
            onClick={(event) => {
              event.preventDefault();
              navigate(`/dashboard/director/cashflow?${impactQuery}`);
            }}
          >
            Ver impacto no fluxo
          </a>
          <a
            className="director-link-button director-link-button--secondary"
            href={listPath}
            onClick={backToList}
          >
            Voltar a clientes
          </a>
        </div>
      </aside>
    </div>
  </main>;
};

export default CustomerReceiptDetail;
